/**
 * AB#1797 (CR-2026-05-29) — TanStack mutation hook for the staff Mark-Paid flow.
 *
 * Idempotency: the caller generates a UUID once per modal mount and passes it.
 * Retries are allowed on 5xx (transient); NOT on 409 (`Payment.AlreadyPaidByStripe`).
 */

import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../lib/queryKeys';
import { markAppointmentPaid, MarkPaidBody, MarkPaidResponse } from '../services/appointmentsList';
import { trackEvent } from '../lib/analytics';

interface MarkPaidVariables {
    appointmentId: number;
    body: MarkPaidBody;
    idempotencyKey: string;
    /** Analytics provenance — 'list' or 'calendar' */
    source: 'list' | 'calendar';
}

export function useMarkAppointmentPaid() {
    const queryClient = useQueryClient();

    return useMutation<MarkPaidResponse, unknown, MarkPaidVariables>({
        mutationFn: ({ appointmentId, body, idempotencyKey }) =>
            markAppointmentPaid(appointmentId, body, idempotencyKey),

        onSuccess: (data, variables) => {
            // UAT BUG #2 fix (2026-05-30): the previous queryKey was
            // `queryKeys.appointments.listV2()` (no params). TanStack matches
            // by deep equality of the FIRST N keys, so the active query keyed
            // `['appointments', 'listV2', { paymentStatus: 'Unpaid', ... }]`
            // never matched `['appointments', 'listV2', undefined]` — the
            // invalidate fired against a "phantom" key and the cached page
            // data never refetched. Symptom: the row stayed Unpaid in the UI
            // even after the BE returned 200 Paid.
            //
            // Fix: invalidate the broader prefix `['appointments']` so EVERY
            // appointments-related cache entry refetches. Cheap — the page
            // only has one active list-v2 query at a time.
            queryClient.invalidateQueries({ queryKey: queryKeys.appointments.all });

            // D1 analytics — no PII.
            try {
                trackEvent('appointments.markedPaid', {
                    appointmentId: variables.appointmentId,
                    paymentMethod: variables.body.paymentMethod,
                    source: variables.source,
                });
            } catch {
                // swallow — analytics must not break UX
            }
        },

        // Do NOT retry on 409 (Stripe-race); retry up to 2× on 5xx.
        retry: (failureCount, error) => {
            const status = (error as { response?: { status?: number } })?.response?.status;
            if (status === 409) return false;
            return failureCount < 2;
        },
    });
}
